import { db, isoMs, SYNCED_TABLES, winsRemote, type OutboxItem, type SyncedTable } from '@/lib/db';
import { supabase } from '@/lib/supabase';
import { TABLE_SPECS, toLocal, toRemote, type RemoteRow } from './tables';
import type { Table } from 'dexie';

/** Cursor de bajada POR TABLA: con uno compartido, una tabla adelantaba a otra. */
const PULL_CURSOR_KEY = 'sync:cursor';
/** Un lote grande no acelera nada y arriesga timeouts en la Edge. */
const BATCH_SIZE = 200;
/** Tope de filas por petición de PostgREST; se pagina hasta vaciar. */
const PAGE_SIZE = 1000;
/**
 * Margen hacia atrás al pedir cambios. `synced_at` lo pone el servidor al
 * escribir, pero una transacción puede confirmarse después de otra que empezó
 * más tarde, con una marca algo anterior a la última vista. Bajar dos veces la
 * misma fila no cuesta nada: gana la última escritura.
 */
const PULL_OVERLAP_MS = 60_000;
/** Backoff de reintentos (SPEC 3.2): 30 s, 1 min, 2 min... hasta 15 min. */
const BACKOFF_BASE_MS = 30_000;
const BACKOFF_MAX_MS = 15 * 60_000;

export type SyncState = 'idle' | 'syncing' | 'offline' | 'error' | 'disabled';

/** Fila tal como llega de Postgres: con la marca que puso el servidor. */
type PulledRow = RemoteRow & { synced_at: string };

let running = false;
let failures = 0;
let retryAt = 0;

/** Olvida los fallos: al volver la conexión se reintenta ya, sin esperar. */
export function resetSyncBackoff(): void {
  failures = 0;
  retryAt = 0;
}

async function currentUserId(): Promise<string | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.user.id ?? null;
}

export async function pendingCount(): Promise<number> {
  return db.outbox.count();
}

/**
 * Sube lo pendiente, tabla a tabla. Si una tabla falla, sus entradas se quedan
 * en la cola y las demás suben igual: un fallo en los pesos no debe retener
 * las comidas. La cola es lo que garantiza que nada se pierde sin cobertura.
 */
async function push(userId: string): Promise<void> {
  const client = supabase;
  if (!client) return;

  for (;;) {
    const items = await db.outbox.orderBy('seq').limit(BATCH_SIZE).toArray();
    if (items.length === 0) return;

    const byTable = new Map<SyncedTable, OutboxItem[]>();
    for (const item of items) {
      byTable.set(item.table, [...(byTable.get(item.table) ?? []), item]);
    }

    const failed: string[] = [];
    for (const [table, group] of byTable) {
      const seqs = group.map((item) => item.seq!);
      const payload = group.map((item) =>
        toRemote(table, item.payload as Record<string, unknown>, userId),
      );
      const { error } = await client.from(TABLE_SPECS[table].remote).upsert(payload, {
        onConflict: 'user_id,id',
      });

      if (error) {
        await markTried(seqs, error.message);
        failed.push(`${table}: ${error.message}`);
        continue;
      }
      // Solo se borran las entradas que se subieron. Una edición hecha durante
      // la subida tiene `seq` nuevo (ver `putAndQueue`) y se queda en la cola.
      await db.outbox.bulkDelete(seqs);
    }

    // ponytail: una fila que el servidor rechaza siempre bloquea su tabla; si
    // pasa, subir fila a fila la tabla que falle para aislarla.
    if (failed.length > 0) throw new Error(`No se pudo subir ${failed.join('; ')}`);
    if (items.length < BATCH_SIZE) return;
  }
}

async function markTried(seqs: number[], message: string): Promise<void> {
  await db.transaction('rw', db.outbox, async () => {
    for (const seq of seqs) {
      const item = await db.outbox.get(seq);
      if (item) await db.outbox.put({ ...item, tries: item.tries + 1, lastError: message });
    }
  });
}

/** PostgREST exige comillas para valores con `.`, `:` o `,` dentro de `or`. */
function quoted(value: string): string {
  return `"${value}"`;
}

/**
 * Baja lo que haya cambiado desde la última vez, tabla a tabla y página a
 * página. El cursor es `synced_at`, la hora del SERVIDOR al escribir, no el
 * `updated_at` del cliente: con relojes de móvil atrasados, filtrar por
 * `updated_at` dejaba filas sin bajar para siempre. `updated_at` sigue
 * decidiendo quién gana (SPEC 3.2).
 */
async function pull(userId: string): Promise<void> {
  const client = supabase;
  if (!client) return;

  for (const table of SYNCED_TABLES) {
    const cursorKey = `${PULL_CURSOR_KEY}:${table}`;
    const meta = await db.meta.get(cursorKey);
    const since =
      typeof meta?.value === 'string'
        ? new Date(isoMs(meta.value) - PULL_OVERLAP_MS).toISOString()
        : null;
    let last: PulledRow | null = null;

    for (;;) {
      let query = client.from(TABLE_SPECS[table].remote).select('*').eq('user_id', userId);
      if (last) {
        // Paginación por clave (synced_at, id): con OFFSET, una fila editada a
        // mitad de la bajada se movía al final y otra se quedaba sin bajar.
        const at = quoted(last.synced_at);
        query = query.or(`synced_at.gt.${at},and(synced_at.eq.${at},id.gt.${quoted(last.id)})`);
      } else if (since) {
        query = query.gt('synced_at', since);
      }

      const { data, error } = await query
        .order('synced_at', { ascending: true })
        .order('id', { ascending: true })
        .limit(PAGE_SIZE);
      if (error) throw new Error(`No se pudo bajar ${table}: ${error.message}`);

      const rows = (data ?? []) as PulledRow[];
      if (rows.length === 0) break;

      await applyRemote(table, rows);
      last = rows.at(-1)!;
      // Por página: si la red se corta a mitad, no se vuelve a empezar de cero.
      await db.meta.put({ key: cursorKey, value: last.synced_at });
      if (rows.length < PAGE_SIZE) break;
    }
  }
}

/** Gana la última escritura: si lo local es más nuevo, lo remoto se ignora. */
async function applyRemote(table: SyncedTable, rows: readonly PulledRow[]): Promise<void> {
  const target = db[table] as unknown as Table<Record<string, unknown>, string>;

  await db.transaction('rw', target, async () => {
    for (const remote of rows) {
      const local = await target.get(remote.id);
      const localUpdatedAt = typeof local?.updatedAt === 'string' ? local.updatedAt : '';
      if (local && !winsRemote(localUpdatedAt, remote.updated_at)) continue;
      await target.put(toLocal(table, remote));
    }
  });
}

/**
 * Un ciclo completo. Silencioso por diseño: sin conexión no es un error, es el
 * estado normal de una app que se usa en un gimnasio en el sótano.
 */
export async function syncOnce(): Promise<SyncState> {
  if (!supabase) return 'disabled';
  // `=== false`: donde no se sabe (undefined), se intenta.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
  if (running) return 'syncing';
  // En backoff tras un fallo: no se machaca al servidor.
  if (Date.now() < retryAt) return 'error';

  running = true;
  try {
    const userId = await currentUserId();
    if (!userId) return 'disabled';
    await push(userId);
    await pull(userId);
    resetSyncBackoff();
    return 'idle';
  } catch (error) {
    failures++;
    retryAt = Date.now() + Math.min(BACKOFF_BASE_MS * 2 ** (failures - 1), BACKOFF_MAX_MS);
    console.warn('Sincronización fallida, se reintentará', error);
    return 'error';
  } finally {
    running = false;
  }
}

/**
 * Arranca el ciclo: al volver la conexión, cada pocos minutos, y al reabrir la
 * pestaña. Devuelve la función de limpieza.
 */
export function startSync(intervalMs = 120_000): () => void {
  if (!supabase) return () => undefined;

  const tick = () => void syncOnce();
  // SPEC 3.2: "se drena al recuperar conexión", sin esperar al backoff.
  const onOnline = () => {
    resetSyncBackoff();
    tick();
  };
  const onVisible = () => {
    if (document.visibilityState === 'visible') tick();
  };

  tick();
  const timer = setInterval(tick, intervalMs);
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisible);

  return () => {
    clearInterval(timer);
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
